import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface PoiDto {
	id: string;
	worldId: string;
	regionId: string;
	regionName: string | null;
	typeId: string | null;
	typeName: string | null;
	name: string;
	description: string | null;
	gmNotes: string | null;
	imageAssetId: string | null;
	mapAssetId: string | null;
	pdfAssetId: string | null;
	foundryId: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface PoiListItemDto {
	id: string;
	name: string;
	regionId: string;
	regionName: string | null;
	typeId: string | null;
	typeName: string | null;
	foundryId: string | null;
	updatedAt: string;
}

export interface PoisListResponse {
	pois: PoiListItemDto[];
	total: number;
}

export interface PoiGetResponse {
	poi: PoiDto;
}

export interface ListPoisParams {
	search?: string;
	regionId?: string;
	typeId?: string;
	godId?: string;
	sortBy?: string;
	sortDirection?: 'asc' | 'desc';
	limit?: number;
	offset?: number;
	format?: 'list' | 'full';
}

export interface CreatePoiRequest {
	name: string;
	regionId: string;
	typeId?: string | null;
	description?: string | null;
	gmNotes?: string | null;
	imageAssetId?: string | null;
	mapAssetId?: string | null;
	pdfAssetId?: string | null;
	foundryId?: string | null;
}

export interface UpdatePoiRequest {
	name: string;
	regionId: string;
	typeId?: string | null;
	description?: string | null;
	gmNotes?: string | null;
	imageAssetId?: string | null;
	mapAssetId?: string | null;
	pdfAssetId?: string | null;
	foundryId?: string | null;
}

@Injectable({ providedIn: 'root' })
export class PointsOfInterestService {
	constructor(private readonly http: HttpClient) {}

	list(worldId: string, params?: ListPoisParams): Observable<PoisListResponse> {
		let httpParams = new HttpParams();

		if (params?.search) httpParams = httpParams.set('search', params.search);
		if (params?.regionId) httpParams = httpParams.set('regionId', params.regionId);
		if (params?.typeId) httpParams = httpParams.set('typeId', params.typeId);
		if (params?.godId) httpParams = httpParams.set('godId', params.godId);
		if (params?.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
		if (params?.sortDirection) httpParams = httpParams.set('sortDirection', params.sortDirection);
		if (params?.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
		if (params?.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString());
		if (params?.format) httpParams = httpParams.set('format', params.format);

		return this.http.get<PoisListResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/points-of-interest`, {
			params: httpParams,
		});
	}

	get(worldId: string, poiId: string): Observable<PoiGetResponse> {
		return this.http.get<PoiGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/points-of-interest/${poiId}`);
	}

	create(worldId: string, request: CreatePoiRequest): Observable<PoiGetResponse> {
		return this.http.post<PoiGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/points-of-interest`, request);
	}

	update(worldId: string, poiId: string, request: UpdatePoiRequest): Observable<PoiGetResponse> {
		return this.http.put<PoiGetResponse>(
			`${environment.api.baseUrl}/v1/worlds/${worldId}/points-of-interest/${poiId}`,
			request
		);
	}

	delete(worldId: string, poiId: string): Observable<void> {
		return this.http.delete<void>(`${environment.api.baseUrl}/v1/worlds/${worldId}/points-of-interest/${poiId}`);
	}
}
